<template>
  <div class="card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5 class="mb-0">{{ $t('profile.about.title') }}</h5>
      <div v-if="canEditTenantInfo && !editing">
        <button class="btn btn-sm btn-primary" @click="startEditing">
          <i class="ti ti-edit me-1"></i>
          {{ $t('common.edit') }}
        </button>
      </div>
    </div>
    <div class="card-body">
      <div v-if="isLoading" class="text-center py-4">
        <span class="spinner-border" role="status"></span>
        <p class="mt-2 mb-0">Loading about section...</p>
      </div>
      <div v-else>
        <!-- View mode -->
        <div v-if="!editing" class="about-content">
          <p v-if="about" class="mb-0" v-html="formatAbout(about)"></p>
          <p v-else class="text-muted mb-0">{{ $t('profile.about.noDescription') }}</p>
        </div>
        
        <!-- Edit mode -->
        <div v-else>
          <textarea 
            class="form-control" 
            v-model="editAbout" 
            rows="5" 
            :placeholder="$t('profile.about.placeholder')"
            :disabled="!canEditTenantInfo"
          ></textarea>
          
          <div class="mt-3 text-end" v-if="canEditTenantInfo">
            <button class="btn btn-outline-secondary me-2" @click="cancelEditing">
              {{ $t('common.cancel') }}
            </button>
            <button class="btn btn-primary" @click="saveAbout" :disabled="isSaving">
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('common.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import tenantInfoService from '@/services/tenantInfoService';
import { usePermissions } from '@/composables/usePermissions';

export default {
  name: 'ProfileAboutCard',
  props: {
    about: {
      type: String,
      default: ''
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['updated'],
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const { hasPermission } = usePermissions();
    
    // Permission check
    const canEditTenantInfo = computed(() => hasPermission('Supplier.TenantInfo.Set'));
    
    const editing = ref(false);
    const editAbout = ref(props.about || '');
    const isSaving = ref(false);

    const startEditing = () => { 
      editing.value = true; 
      editAbout.value = props.about || '';
    };
    
    const cancelEditing = () => { 
      editing.value = false; 
      editAbout.value = props.about || '';
    };
    
    const saveAbout = async () => {
      isSaving.value = true;
      try {
        // Update only the about property via tenant extra properties
        await tenantInfoService.setExtraProperty('About', editAbout.value);
        
        editing.value = false;
        
        // Emit updated event to parent to refresh data
        emit('updated');
        
        store.dispatch('notify', {
          message: 'About section updated successfully',
          type: 'success'
        });
      } catch (error) {
        console.error('Error updating about:', error);
        store.dispatch('notify', {
          message: 'Failed to update about section',
          type: 'error'
        });
      } finally {
        isSaving.value = false;
      }
    };

    const formatAbout = (about) => {
      // Implement your formatting logic here
      return about;
    };

    return {
      editing,
      editAbout,
      isSaving,
      startEditing,
      cancelEditing,
      saveAbout,
      formatAbout,
      t,
      canEditTenantInfo
    };
  }
};
</script> 